import React from "react";
import { FiChevronRight, FiPhone, FiMail, FiMapPin, FiArrowUp } from "react-icons/fi";
import { FaInstagram, FaFacebook } from "react-icons/fa";
import logo from "../assets/logo.png";
import { useLanguage } from "../context/LanguageContext";

export default function Footer({ onOpenDocuments }) {
  const { t } = useLanguage();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navLinks = [
    { title: t("nav.tariffs"), href: "#tariffs" },
    { title: t("nav.speedtest"), href: "#speedtest" },
    { title: t("nav.coverage"), href: "#coverage" },
    { title: t("nav.about"), href: "#about" },
    { title: t("nav.contacts"), href: "#contacts" },
  ];

  return (
    <footer className="w-full bg-slate-950 text-white pt-16 pb-12 border-t border-slate-800 relative z-10">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 pb-12 border-b border-slate-850">
          {/* Column 1: Brand Info */}
          <div className="lg:col-span-5 space-y-5">
            <div className="bg-white/95 p-3 rounded-2xl inline-block">
              <img src={logo} alt="Real Time Telecom" className="h-9 w-auto" />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed max-w-sm">
              {t("footer.desc")}
            </p>
            <div className="flex items-center space-x-3 pt-2">
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.facebook.com/realtime.internet"
                className="w-10 h-10 rounded-xl bg-slate-800 hover:bg-brandRed text-slate-300 hover:text-white flex items-center justify-center transition-colors"
                aria-label="Facebook"
              >
                <FaFacebook size={18} />
              </a>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.instagram.com/realtime.am"
                className="w-10 h-10 rounded-xl bg-slate-800 hover:bg-brandRed text-slate-300 hover:text-white flex items-center justify-center transition-colors"
                aria-label="Instagram"
              >
                <FaInstagram size={18} />
              </a>
            </div>
          </div>

          {/* Column 2: Navigation & Transparency */}
          <div className="lg:col-span-3 space-y-4">
            <h4 className="text-xs font-black uppercase tracking-widest text-white border-b border-slate-800 pb-2">
              {t("footer.quickLinks")}
            </h4>
            <ul className="space-y-2.5 text-xs font-semibold uppercase tracking-wider list-none p-0 m-0 text-slate-400">
              {navLinks.map((link, idx) => (
                <li key={idx}>
                  <a
                    href={link.href}
                    className="hover:text-brandRed transition-colors flex items-center"
                  >
                    <FiChevronRight className="mr-1 text-slate-600" />
                    <span>{link.title}</span>
                  </a>
                </li>
              ))}
              <li className="pt-2 border-t border-slate-850">
                <button
                  onClick={() => onOpenDocuments && onOpenDocuments("reports")}
                  className="hover:text-brandRed transition-colors flex items-center text-xs font-bold uppercase tracking-wider text-slate-300 cursor-pointer"
                >
                  <FiChevronRight className="mr-1 text-brandRed" />
                  <span>{t("footer.reports")}</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => onOpenDocuments && onOpenDocuments("terms")}
                  className="hover:text-brandRed transition-colors flex items-center text-xs font-bold uppercase tracking-wider text-slate-300 cursor-pointer"
                >
                  <FiChevronRight className="mr-1 text-brandRed" />
                  <span>{t("footer.terms")}</span>
                </button>
              </li>
            </ul>
          </div>

          {/* Column 3: Contact Details */}
          <div className="lg:col-span-4 space-y-4">
            <h4 className="text-xs font-black uppercase tracking-widest text-white border-b border-slate-800 pb-2">
              {t("footer.contactInfo")}
            </h4>
            <div className="space-y-3 text-xs text-slate-400">
              <div className="flex items-start space-x-3">
                <FiMapPin className="text-brandRed mt-0.5 flex-shrink-0" size={16} />
                <span>{t("footer.address")}</span>
              </div>
              <div className="flex items-start space-x-3">
                <FiPhone className="text-brandRed mt-0.5 flex-shrink-0" size={16} />
                <div className="space-y-0.5">
                  <a href="tel:+37460666999" className="hover:text-white block font-bold text-slate-300">
                    060 666 999
                  </a>
                  <span className="text-[11px] text-slate-500">
                    091 19 52 65 | 094 19 52 65 | 055 19 52 65
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <FiMail className="text-brandRed flex-shrink-0" size={16} />
                <div className="flex space-x-3">
                  <a href="mailto:info@realtime.am" className="hover:text-white transition-colors">
                    info@realtime.am
                  </a>
                  <span className="text-slate-600">|</span>
                  <a href="mailto:hr@realtime.am" className="hover:text-white transition-colors">
                    hr@realtime.am
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-500 gap-4">
          <p>
            © «Ռեալ Թայմ» ՍՊԸ 2001 - {new Date().getFullYear()} : {t("footer.allRights")}
          </p>

          <button
            onClick={scrollToTop}
            className="flex items-center space-x-1.5 text-xs font-bold text-slate-400 hover:text-white transition-colors p-2 rounded-xl bg-slate-900 hover:bg-slate-800 cursor-pointer"
          >
            <span>{t("footer.backToTop")}</span>
            <FiArrowUp size={14} />
          </button>
        </div>
      </div>
    </footer>
  );
}
